import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { authApi } from './api';
import { Loader2, Eye, EyeOff, Home } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useFirebaseAuthLogin } from './useFirebaseAuthLogin';
import { signUpWithEmailPassword, signInWithGoogle, signInWithFacebook } from '@/config/firebase';

const isAdult = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  const today = new Date();
  let age = today.getFullYear() - d.getFullYear();
  const m = today.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < d.getDate())) age -= 1;
  return age >= 18;
};

const phoneRule = 'El celular debe iniciar con 3 y tener 10 dígitos.';
const passwordRule = 'Contraseña inválida. Mín. 8 caracteres, 1 mayúscula y 1 número.';
const schema = z.object({
  fullName: z.string().min(1, 'Nombre es obligatorio').max(120),
  email: z.string().email('Correo inválido'),
  phone: z
    .string()
    .min(1, 'Celular es obligatorio')
    .refine((v) => /^3\d{9}$/.test(v.replace(/\D/g, '')), { message: phoneRule }),
  birthDate: z
    .string()
    .min(1, 'Fecha de nacimiento es obligatoria')
    .refine((v) => isAdult(v), { message: 'Debes ser mayor de 18 años.' }),
  password: z
    .string()
    .regex(/^(?=.*[A-Z])(?=.*\d).{8,}$/, passwordRule),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, { message: 'Las contraseñas no coinciden.', path: ['confirmPassword'] });

type FormData = z.infer<typeof schema>;

export default function SignUpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isPolicyOpen, setIsPolicyOpen] = useState(false);
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [ssoLoading, setSsoLoading] = useState<'google' | 'facebook' | null>(null);
  const { completeLogin, isLoading: isCompletingLogin } = useFirebaseAuthLogin();
  const redirectState = (location.state || {}) as {
    returnTo?: string;
    animalId?: number;
    shelterId?: number;
    prefill?: {
      fullName?: string;
      email?: string;
      phone?: string;
      birthDate?: string;
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });
  const email = watch('email');
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  useEffect(() => {
    const value = email?.trim() || '';
    if (!value || !/^[^@]+@[^@]+\.[^@]+$/.test(value)) {
      clearErrors('email');
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        setIsCheckingEmail(true);
        const exists = await authApi.checkEmailExists(value);
        if (cancelled) return;
        if (exists) {
          setError('email', { type: 'manual', message: 'El correo ya está registrado.' });
        } else {
          clearErrors('email');
        }
      } catch {
        if (!cancelled) clearErrors('email');
      } finally {
        if (!cancelled) setIsCheckingEmail(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [email, setError, clearErrors]);

  useEffect(() => {
    const prefill = redirectState.prefill;
    if (!prefill) return;
    if (prefill.fullName) setValue('fullName', prefill.fullName);
    if (prefill.email) setValue('email', prefill.email);
    if (prefill.phone) setValue('phone', prefill.phone);
    if (prefill.birthDate) setValue('birthDate', prefill.birthDate);
  }, [redirectState.prefill, setValue]);

  const goToDestination = () => {
    const hasSelectedAnimal = !!redirectState.animalId;
    const destination = hasSelectedAnimal ? '/adoption/wizard' : (redirectState.returnTo || '/adopt');
    const nextState = hasSelectedAnimal
      ? { animalId: redirectState.animalId, shelterId: redirectState.shelterId }
      : undefined;
    navigate(destination, nextState ? { state: nextState } : undefined);
  };

  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    try {
      setIsCheckingEmail(true);
      const exists = await authApi.checkEmailExists(data.email);
      if (exists) {
        setError('email', { type: 'manual', message: 'El correo ya está registrado.' });
        setIsCheckingEmail(false);
        return;
      }
      setIsCheckingEmail(false);
      const phoneDigits = data.phone.replace(/\D/g, '').slice(0, 10);

      const idToken = await signUpWithEmailPassword(data.email, data.password);
      await completeLogin(idToken, {
        fullName: data.fullName,
        phone: phoneDigits,
        birthDate: data.birthDate,
      });

      toast({
        title: 'Cuenta creada',
        description: 'Bienvenido a Sabuesos.',
      });
      goToDestination();
    } catch (error: any) {
      setIsCheckingEmail(false);
      const message =
        error?.code === 'auth/email-already-in-use'
          ? 'El correo ya está registrado.'
          : error?.response?.data?.message ||
            error?.response?.data?.error?.message ||
            'No se pudo crear la cuenta.';
      toast({ variant: 'destructive', title: 'Error', description: message });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoSignUp = async (provider: 'google' | 'facebook') => {
    setSsoLoading(provider);
    try {
      const idToken = await (provider === 'google' ? signInWithGoogle() : signInWithFacebook());
      await completeLogin(idToken);

      toast({
        title: '¡Bienvenido!',
        description: 'Tu cuenta está lista.',
      });
      goToDestination();
    } catch (error: any) {
      if (error?.code === 'auth/popup-closed-by-user') return;
      const message = error?.response?.data?.message || error?.message || 'No se pudo continuar.';
      toast({ variant: 'destructive', title: 'Error', description: message });
    } finally {
      setSsoLoading(null);
    }
  };

  const busy = isLoading || isCompletingLogin || !!ssoLoading;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-hero p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-4">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card/80 backdrop-blur-sm border border-border/50 text-sm text-muted-foreground hover:text-foreground hover:bg-card transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <Home className="h-4 w-4" />
            <span>Volver al inicio</span>
          </Link>
        </div>

        <Card className="w-full max-w-md p-8 shadow-xl space-y-6">
          <div className="flex flex-col items-center">
            <img src="/logo-sabuesos.png" alt="Sabuesos" className="w-16 h-16 mb-4 object-contain" />
            <h1 className="text-2xl font-bold text-foreground">Crear cuenta</h1>
            <p className="text-muted-foreground mt-2 text-center">
              Regístrate para comenzar a usar Sabuesos
            </p>
          </div>

          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={() => handleSsoSignUp('google')}
            >
              {ssoLoading === 'google' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continuar con Google
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              disabled={busy}
              onClick={() => handleSsoSignUp('facebook')}
            >
              {ssoLoading === 'facebook' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Continuar con Facebook
            </Button>
            <div className="relative py-2">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">o completa tus datos</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName">Nombre completo</Label>
            <Input id="fullName" placeholder="Tu nombre" {...register('fullName')} aria-invalid={!!errors.fullName} />
            {errors.fullName && <p className="text-sm text-destructive">{errors.fullName.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Correo</Label>
            <Input id="email" type="email" placeholder="tu@email.com" {...register('email')} aria-invalid={!!errors.email} />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            {isCheckingEmail && <p className="text-xs text-muted-foreground">Verificando disponibilidad...</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Celular</Label>
            <Input
              id="phone"
              placeholder="000 000 0000"
              inputMode="tel"
              {...register('phone', {
                onChange: (e) => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                  const part1 = digits.slice(0, 3);
                  const part2 = digits.slice(3, 6);
                  const part3 = digits.slice(6, 10);
                  const formatted = [part1, part2, part3].filter(Boolean).join(' ');
                  setValue('phone', formatted, { shouldDirty: true, shouldValidate: true });
                },
              })}
              value={watch('phone') || ''}
              aria-invalid={!!errors.phone}
            />
            {errors.phone && <p className="text-sm text-destructive">{errors.phone.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="birthDate">Fecha de nacimiento</Label>
            <Input id="birthDate" type="date" {...register('birthDate')} aria-invalid={!!errors.birthDate} />
            {errors.birthDate && <p className="text-sm text-destructive">{errors.birthDate.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Contraseña</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPass ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('password')}
                aria-invalid={!!errors.password}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPass((v) => !v)}
                className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                aria-label={showPass ? 'Ocultar contraseña' : 'Mostrar contraseña'}
              >
                {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">
              Mínimo 8 caracteres, 1 letra mayúscula y 1 número.
            </p>
            {errors.password && <p className="text-sm text-destructive">{errors.password.message || passwordRule}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirmar contraseña</Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type={showConfirm ? 'text' : 'password'}
                placeholder="••••••••"
                {...register('confirmPassword')}
                aria-invalid={!!errors.confirmPassword}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute inset-y-0 right-2 flex items-center text-muted-foreground hover:text-foreground"
                aria-label={showConfirm ? 'Ocultar confirmación' : 'Mostrar confirmación'}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword.message}</p>}
          </div>

          <p className="text-xs text-muted-foreground">
            Al registrarte confirmas estar de acuerdo con nuestra{' '}
            <button type="button" className="text-primary hover:underline" onClick={() => setIsPolicyOpen(true)}>
              Política de Protección de Datos
            </button>{' '}
            y los{' '}
            <button type="button" className="text-primary hover:underline" onClick={() => setIsTermsOpen(true)}>
              Términos y Condiciones
            </button>.
          </p>

          <Button type="submit" className="w-full" disabled={busy} aria-busy={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creando cuenta...
              </>
            ) : (
              'Crear cuenta'
            )}
          </Button>
          </form>

          <p className="text-sm text-muted-foreground text-center">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" state={redirectState} className="text-primary hover:underline">Inicia sesión</Link>
          </p>
        </Card>
      </div>

      <Dialog open={isPolicyOpen} onOpenChange={setIsPolicyOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Política de Protección de Datos</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>1. Usamos tus datos únicamente para administrar tu cuenta y el proceso de adopción.</p>
            <p>2. No compartimos tu información con terceros sin tu consentimiento, salvo requerimientos legales.</p>
            <p>3. Puedes solicitar la eliminación o actualización de tus datos escribiendo a soporte.</p>
            <p>4. Tomamos medidas razonables de seguridad para proteger tu información.</p>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isTermsOpen} onOpenChange={setIsTermsOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Términos y Condiciones</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>1. Al registrar tu cuenta aceptas usar la plataforma de forma responsable y veraz.</p>
            <p>2. El registro como “customer” es para interesados en adopción; no otorga acceso administrativo.</p>
            <p>3. Podemos actualizar estos términos; el uso continuado implica aceptación de las nuevas versiones.</p>
            <p>4. El incumplimiento puede resultar en suspensión o bloqueo de la cuenta.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
